import { defineMessages, useIntl } from 'react-intl-next';

import type { Route } from '@confluence/route';
import {
	SPACE_PERMISSIONS_SETTINGS,
	ADMIN_COMPANY_HUB_PERMISSIONS,
} from '@confluence/named-routes';
import { SpacePermissionsTabKey } from '@confluence/space-permissions/entry-points/spacePermissionsKeys';
import { isCompanyHubSpaceKey } from '@confluence/route-manager/entry-points/companyHubUtils';
import { useSpaceKey } from '@confluence/space-utils';
import { fg } from '@confluence/feature-gating';

import { KeysOfWebItemsWithExtraUrls } from '../helpers/webItem';

import type {
	SpaceSettingsWebItemSectionsFragment_webItemSections,
	SpaceSettingsWebItemSectionsFragment_webItemSections_items,
} from './__types__/SpaceSettingsWebItemSectionsFragment';
import type { SpaceSettingsWebItemSectionsInjector } from './SpaceSettingsWebItemSectionsInjector';

export const SPACE_PERMISSIONS_WEBITEM_SECTION_ID = '-system.space.tools-permissions';
const SPACE_PERMISSIONS_COMPLETE_KEY = 'confluence-frontend.';
const USERS_TAB_ID = 'space-permissions-users';
const GROUPS_TAB_ID = 'space-permissions-groups';
const GUESTS_TAB_ID = 'space-permissions-guests';
const ANONYMOUS_TAB_ID = 'space-permissions-anonymous-access';

const { GENERAL_PERMISSIONS_WEBITEM_COMPLETE_KEY } = KeysOfWebItemsWithExtraUrls;

const i18n = defineMessages({
	spacePermissionsWebItemUsersLink: {
		id: 'space-settings.web-item-section.space-permissions.web-item.users',
		defaultMessage: 'Users',
		description: 'Label for the web item link for the users tab',
	},
	spacePermissionsWebItemGroupsLink: {
		id: 'space-settings.web-item-section.space-permissions.web-item.groups',
		defaultMessage: 'Groups',
		description: 'Label for the web item link for the groups tab',
	},
	spacePermissionsWebItemGuestsLink: {
		id: 'space-settings.web-item-section.space-permissions.web-item.guests',
		defaultMessage: 'Guests',
		description: 'Label for the web item link for the guests tab',
	},
	spacePermissionsWebItemAnonymousAccessLink: {
		id: 'space-settings.web-item-section.space-permissions.web-item.anonymous-access',
		defaultMessage: 'Anonymous access',
		description: 'Label for the web item link for the anonymous access tab',
	},
	hubPermissionsWebItemSectionTitle: {
		id: 'space-settings.web-item-section.hub-permissions.web-item.title',
		defaultMessage: 'Hub permissions',
		description: 'Label for the hub permissions web item section',
	},
});

const addSpacePermissionsWebItems = (
	itemsToAdd: SpaceSettingsWebItemSectionsFragment_webItemSections_items[],
	section: SpaceSettingsWebItemSectionsFragment_webItemSections,
) => {
	if (itemsToAdd?.length) {
		//remove the "General" link and add the rest of the space permissions tabs
		const isGeneralLink = (
			item: SpaceSettingsWebItemSectionsFragment_webItemSections_items | null,
		) => item?.completeKey == GENERAL_PERMISSIONS_WEBITEM_COMPLETE_KEY;
		const generalLinkIndex = section.items.findIndex(isGeneralLink);

		if (generalLinkIndex !== -1) {
			// XXX Don't mutate the input section! May be shared across invocations.
			const items = [...section.items];
			items.splice(generalLinkIndex, 1, ...itemsToAdd);
			return {
				...section,
				items,
			};
		}
	}
	return section;
};

export const useSpacePermissionsWebItems: SpaceSettingsWebItemSectionsInjector = ({
	webItemSections,
}) => {
	const spaceKey = useSpaceKey();
	const sectionIndex =
		webItemSections?.findIndex((section) => section?.id === SPACE_PERMISSIONS_WEBITEM_SECTION_ID) ??
		-1;
	const inputSection = webItemSections?.[sectionIndex] ?? null;
	const outputSection = useSpacePermissionsWebItemSection(spaceKey, inputSection);

	if (inputSection && inputSection !== outputSection) {
		// XXX Don't mutate the input webItemSections! May be shared across invocations.
		webItemSections = webItemSections!.slice();
		webItemSections[sectionIndex] = outputSection;
	}

	return webItemSections;
};

function useSpacePermissionsWebItemSection(
	spaceKey: string | null | undefined,
	section: SpaceSettingsWebItemSectionsFragment_webItemSections | null,
) {
	const intl = useIntl();

	if (!section) return section;

	const isCompanyHub = isCompanyHubSpaceKey(spaceKey);
	if (isCompanyHub) {
		// Company Hub hides its space from the user so the label/title "Space
		// permissions" is undesirable.
		section = {
			...section,
			label: intl.formatMessage(i18n.hubPermissionsWebItemSectionTitle),
		};
	}

	if (!spaceKey) {
		return section;
	}

	const getPermissionSettingsWebItems = (
		route: Route,
	): SpaceSettingsWebItemSectionsFragment_webItemSections_items[] => [
		{
			id: USERS_TAB_ID,
			label: intl.formatMessage(i18n.spacePermissionsWebItemUsersLink),
			url: route.toUrl({
				spaceKey,
				tab: SpacePermissionsTabKey.Users,
			}),
			completeKey: SPACE_PERMISSIONS_COMPLETE_KEY + USERS_TAB_ID,
			icon: null,
		},
		{
			id: GROUPS_TAB_ID,
			label: intl.formatMessage(i18n.spacePermissionsWebItemGroupsLink),
			url: route.toUrl({
				spaceKey,
				tab: SpacePermissionsTabKey.Groups,
			}),
			completeKey: SPACE_PERMISSIONS_COMPLETE_KEY + GROUPS_TAB_ID,
			icon: null,
		},
		{
			id: GUESTS_TAB_ID,
			label: intl.formatMessage(i18n.spacePermissionsWebItemGuestsLink),
			url: route.toUrl({
				spaceKey,
				tab: SpacePermissionsTabKey.Guests,
			}),
			completeKey: SPACE_PERMISSIONS_COMPLETE_KEY + GUESTS_TAB_ID,
			icon: null,
		},
		{
			id: ANONYMOUS_TAB_ID,
			label: intl.formatMessage(i18n.spacePermissionsWebItemAnonymousAccessLink),
			url: SPACE_PERMISSIONS_SETTINGS.toUrl({
				spaceKey,
				tab: SpacePermissionsTabKey.AnonymousAccess,
			}),
			completeKey: SPACE_PERMISSIONS_COMPLETE_KEY + ANONYMOUS_TAB_ID,
			icon: null,
		},
	];

	const itemsToAdd = getPermissionSettingsWebItems(
		isCompanyHub ? ADMIN_COMPANY_HUB_PERMISSIONS : SPACE_PERMISSIONS_SETTINGS,
	);

	section = addSpacePermissionsWebItems(itemsToAdd, section);

	if (isCompanyHub) {
		// Only keep Users, Groups, and Guests tabs for Company Hub.
		const allowedTabs = [USERS_TAB_ID, GROUPS_TAB_ID, GUESTS_TAB_ID];
		section = {
			...section,
			items: section.items?.filter((item) => item?.id && allowedTabs.includes(item.id)),
		};
	} else if (fg('cc_perms_exp_rbac_fe_milestone_2')) {
		//if RBAC is enabled, we want to actually remove the users, groups, and guests tabs
		const excludedTabs = [USERS_TAB_ID, GROUPS_TAB_ID, GUESTS_TAB_ID];
		section = {
			...section,
			items: section.items?.filter((item) => !item?.id || !excludedTabs.includes(item.id)),
		};
	}
	return section;
}
